// Bee behaviour (MB.5). PURE (ENG-1): the day-cycle + pollination + anger state
// machine a bee runs, faithful to Minecraft's bee AI. By day a nectar-less bee
// seeks a flower, pollinates, then carries the nectar home to its hive (where the
// deposit boosts nearby crop growth); at night or in rain it heads home to rest.
// Provoked (struck, or its nest broken) it turns angry and stings once — losing
// its stinger and dying shortly after. All decisions are pure functions of the
// bee's context, so the state machine is unit-testable without an entity core.

export type BeeState = 'wander' | 'to_flower' | 'pollinate' | 'to_hive' | 'in_hive' | 'angry';

export interface BeeContext {
  /** Overworld daytime (bees are diurnal). */
  isDay: boolean;
  /** Raining/thundering — bees shelter in the hive. */
  raining: boolean;
  /** Carrying pollen from a flower (→ must return to the hive to deposit). */
  hasNectar: boolean;
  /** Recently attacked, or its nest was disturbed → turns hostile. */
  provoked: boolean;
  /** A pollinatable flower is within search range. */
  flowerInRange: boolean;
  /** Standing on/at that flower now. */
  atFlower: boolean;
  /** At the home hive now. */
  atHive: boolean;
}

/**
 * The bee's current goal. Anger overrides everything; otherwise a bee that is
 * done for the day (night/rain) or laden with nectar heads to the hive, a bee at
 * a flower pollinates, one that can see a flower approaches it, and an idle bee
 * wanders.
 */
export function beeGoal(ctx: BeeContext): BeeState {
  if (ctx.provoked) return 'angry';
  const goHome = !ctx.isDay || ctx.raining || ctx.hasNectar;
  if (goHome) return ctx.atHive ? 'in_hive' : 'to_hive';
  if (ctx.atFlower) return 'pollinate';
  if (ctx.flowerInRange) return 'to_flower';
  return 'wander';
}

/** Pollinating flips the bee to nectar-laden (it then heads home). */
export function collectNectar(): { hasNectar: true } {
  return { hasNectar: true };
}

/** Growth-boost radius applied around a hive when a bee deposits nectar (MC: a
 *  bee arriving with nectar bonemeal-nudges crops within this many blocks). */
export const HIVE_GROWTH_RADIUS = 2;

/** Depositing at the hive consumes the nectar and yields honey progress. */
export function depositNectar(honeyLevel: number): { hasNectar: false; honeyLevel: number } {
  return { hasNectar: false, honeyLevel: Math.min(5, honeyLevel + 1) };
}

export interface StingResult {
  damage: number;
  /** A bee dies a short time after stinging (loses its stinger). */
  diesAfterSting: true;
  /** Poison inflicted on the stung target (seconds by difficulty; easy has none). */
  poisonSeconds: number;
}

/** A bee's sting: damage + poison, and it is now doomed. */
export function sting(difficulty: 'peaceful' | 'easy' | 'normal' | 'hard'): StingResult {
  const poison = difficulty === 'hard' ? 18 : difficulty === 'normal' ? 10 : 0;
  return { damage: 2, diesAfterSting: true, poisonSeconds: poison };
}

// ── Honeybee colony v2: pollination range (M53.4, ARCH §29.11, CNT-4) ───────
// A pure geometry helper — no behaviour change to anything above. The host (host
// world, `blockEntities`) does the actual hive/crop lookups; this only fixes the
// SCAN ORDER, which is what makes "same seed ⇒ same mate twice" hold — `pollenMate`
// (`heredity/crops.ts`) already picks by index into whatever list it is handed, so
// the list's order is the only thing that has to be deterministic.

/** Every integer (dx,dz) offset within Euclidean `range` blocks of the origin,
 *  excluding it, in a fixed scan order (dz outer, dx inner). Shared by "is a hive
 *  within range" and "which crop cells are candidates" — both are "cells within
 *  range of a point" queries over the same geometry. */
export function rangeOffsets(range: number): ReadonlyArray<readonly [number, number]> {
  const out: Array<[number, number]> = [];
  const r = Math.floor(range);
  const r2 = range * range;
  for (let dz = -r; dz <= r; dz++) {
    for (let dx = -r; dx <= r; dx++) {
      if (dx === 0 && dz === 0) continue;
      if (dx * dx + dz * dz <= r2) out.push([dx, dz]);
    }
  }
  return out;
}
